import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  UploadSimple, Scan, SpinnerGap, Warning, Eye, ShoppingBag,
  Hammer, PersonSimpleWalk, Gauge,
} from "@phosphor-icons/react";

const CONTEXTS = [
  { v: "retail_store", l: "Retail Store Floor" },
  { v: "checkout", l: "Checkout / POS" },
  { v: "stock_room", l: "Stock / Restricted" },
  { v: "office_workplace", l: "Office Workplace" },
  { v: "parking_perimeter", l: "Parking / Perimeter" },
];

function riskColor(score) {
  if (score >= 70) return "#FF3B30";
  if (score >= 40) return "#FF9F0A";
  return "#32D74B";
}

function perfColor(score) {
  if (score >= 70) return "#32D74B";
  if (score >= 45) return "#FF9F0A";
  return "#FF3B30";
}

function ScoreBar({ label, score, icon: Icon, invert }) {
  const color = invert ? perfColor(score) : riskColor(score);
  return (
    <div className="bg-[#1A1A1E] border border-white/10 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#A1A1AA]">
          <Icon size={14} weight="bold" /> {label}
        </span>
        <span className="font-mono-data text-sm font-medium" style={{ color }}>{score}</span>
      </div>
      <div className="h-1.5 bg-black/60 overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

export default function VisionAnalyzer() {
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [context, setContext] = useState("retail_store");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/vision/history").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = useCallback((file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use JPEG, PNG or WEBP images only");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setB64(reader.result);
      setResult(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const analyze = async () => {
    if (!b64) { toast.error("Upload a frame first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/vision/analyze", { image_base64: b64, context, camera_name: "Manual Upload" });
      setResult(data.result);
      toast.success("Frame analyzed");
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      {/* Upload */}
      <div className="xl:col-span-5 space-y-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1">Analyze a Frame</h2>
          <p className="text-xs text-[#71717A] mb-4 leading-relaxed">
            Upload a still frame from any camera. SentryVision (Gemini Vision) assesses body language, staff effectiveness, interaction quality, and theft/vandalism risk. In production, frames stream automatically from your live RTSP feeds.
          </p>

          <label
            data-testid="vision-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
            className="block border border-dashed border-white/20 hover:border-[#007AFF] transition-colors cursor-pointer aspect-video relative overflow-hidden"
          >
            <input data-testid="vision-file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#71717A]">
                <UploadSimple size={32} weight="bold" />
                <span className="text-sm mt-2">Drop frame or click to upload</span>
                <span className="font-mono-data text-[10px] mt-1">JPEG · PNG · WEBP</span>
              </div>
            )}
          </label>

          <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] mt-4 mb-2">Camera Context</label>
          <select
            data-testid="vision-context-select"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#007AFF] transition-colors"
          >
            {CONTEXTS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>

          <button
            data-testid="vision-analyze-button"
            onClick={analyze}
            disabled={busy || !b64}
            className="w-full mt-4 bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <SpinnerGap size={18} className="animate-spin" /> : <Scan size={18} weight="bold" />}
            {busy ? "Analyzing Frame" : "Run AI Analysis"}
          </button>
        </div>

        {history.length > 0 && (
          <div className="bg-[#121214] border border-white/10 p-4">
            <h3 className="font-display font-black uppercase tracking-tighter text-xs mb-3 text-[#A1A1AA]">Recent Analyses</h3>
            <div className="space-y-2">
              {history.slice(0, 6).map((h) => (
                <div key={h.id} className="flex items-center justify-between text-xs font-mono-data border-b border-white/5 pb-2">
                  <span className="text-[#A1A1AA] truncate">{h.result?.scene_summary?.slice(0, 40) || h.context}</span>
                  <span style={{ color: riskColor(h.result?.theft_risk?.score || 0) }}>{h.result?.threat_level?.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="xl:col-span-7">
        {!result && !busy && (
          <div className="h-full min-h-[400px] border border-dashed border-white/10 flex flex-col items-center justify-center text-[#71717A]">
            <Eye size={40} weight="thin" />
            <p className="mt-3 text-sm">Analysis results appear here</p>
          </div>
        )}
        {busy && (
          <div className="h-full min-h-[400px] border border-white/10 flex flex-col items-center justify-center text-[#007AFF]">
            <SpinnerGap size={40} className="animate-spin" />
            <p className="mt-3 text-sm font-mono-data uppercase tracking-widest">Processing frame…</p>
          </div>
        )}
        {result && (
          <div data-testid="vision-result" className="space-y-4">
            <div className="bg-[#121214] border border-white/10 p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display font-black uppercase tracking-tighter text-sm">Scene Assessment</h2>
                <span
                  className="font-mono-data text-xs px-2 py-1 border uppercase tracking-wider"
                  style={{ color: riskColor(result.theft_risk?.score || 0), borderColor: riskColor(result.theft_risk?.score || 0) + "66" }}
                >
                  Threat: {result.threat_level}
                </span>
              </div>
              <p className="text-sm text-[#E4E4E7] leading-relaxed">{result.scene_summary}</p>
              <p className="font-mono-data text-xs text-[#71717A] mt-2">People detected: {result.people_count}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ScoreBar label="Employee Effectiveness" score={result.employee_effectiveness?.score ?? 0} icon={Gauge} invert />
              <ScoreBar label="Interaction Quality" score={result.customer_interaction?.quality ?? 0} icon={ShoppingBag} invert />
              <ScoreBar label="Theft Risk" score={result.theft_risk?.score ?? 0} icon={Warning} />
              <ScoreBar label="Vandalism Risk" score={result.vandalism_risk?.score ?? 0} icon={Hammer} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-[#121214] border border-white/10 p-4">
                <h3 className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 flex items-center gap-1.5"><PersonSimpleWalk size={14} weight="bold" /> Body Language Signals</h3>
                <ul className="space-y-1.5">
                  {(result.body_language_signals || []).map((s, i) => (
                    <li key={i} className="text-sm text-[#E4E4E7] flex gap-2"><span className="text-[#007AFF]">›</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#121214] border border-white/10 p-4">
                <h3 className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 flex items-center gap-1.5"><Warning size={14} weight="bold" /> Risk Indicators</h3>
                <ul className="space-y-1.5">
                  {[...(result.theft_risk?.indicators || []), ...(result.vandalism_risk?.indicators || [])].map((s, i) => (
                    <li key={i} className="text-sm text-[#E4E4E7] flex gap-2"><span className="text-[#FF9F0A]">›</span>{s}</li>
                  ))}
                  {[...(result.theft_risk?.indicators || []), ...(result.vandalism_risk?.indicators || [])].length === 0 && (
                    <li className="text-sm text-[#71717A]">None detected</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4">
              <h3 className="text-xs uppercase tracking-widest text-[#3395FF] mb-1">Recommended Action</h3>
              <p className="text-sm text-white">{result.recommended_action}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
